import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Cuboid, CuboidEditor, TransformSpace } from '@abyss/3d-viewer';
import { CuboidClickMode } from '@/types';
import { useSaveCuboid } from '@/modules/cuboids/hooks/useSaveCuboid';
import { useSelectCuboid } from '@/modules/cuboids/hooks/useSelectCuboid';
import { useMakeStyledCuboid } from '@/modules/cuboids/hooks/useMakeStyledCuboid';
import { CuboidBase, CuboidDataHandlers, CuboidState, CuboidStyleHandlers } from '../types';
import { DEFAULT_CUBOID_RESIZE_STEP } from '@/constants';

export type SceneCuboidsProps<CuboidT extends CuboidBase> = {
  cuboidState: CuboidState<CuboidT>;
  dataHandlers: CuboidDataHandlers<CuboidT>;
  styleHandlers: CuboidStyleHandlers<CuboidT>;
};

export const SceneCuboids = <CuboidT extends CuboidBase>({
  cuboidState,
  dataHandlers,
  styleHandlers,
}: SceneCuboidsProps<CuboidT>) => {
  const cuboids = useAtomValue(cuboidState.cuboids);
  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);
  const cuboidTransformMode = useAtomValue(cuboidState.cuboidTransformMode);
  const cuboidClickMode = useAtomValue(cuboidState.cuboidClickMode);
  const isCuboidEditorEnabled = useAtomValue(cuboidState.isCuboidEditorEnabled);
  const referenceCuboid = useAtomValue(cuboidState.referenceCuboid);
  const cuboidResizeStep = useAtomValue(cuboidState.cuboidResizeStep);
  const isAddingWorkscope = useAtomValue(cuboidState.isAddingWorkscope);

  const resizeStep: [x: number, y: number, z: number] = useMemo(() => {
    if (isAddingWorkscope) return cuboidResizeStep;
    return DEFAULT_CUBOID_RESIZE_STEP;
  }, [isAddingWorkscope, cuboidResizeStep]);


  const saveCuboid = useSaveCuboid(dataHandlers);
  const selectCuboid = useSelectCuboid(cuboidState, styleHandlers);
  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const onCuboidClick = useCallback(
    (cuboid: CuboidT) => {
      if (cuboidClickMode === CuboidClickMode.AddCuboid) return;
      selectCuboid(cuboid);
    },
    [cuboidClickMode, selectCuboid]
  );

  const cuboidsComp = useMemo(
    () =>
      cuboids?.map((cuboid) => {
        if (cuboid.id === currentCuboid?.id && isCuboidEditorEnabled) return undefined;
        const styledCuboid = makeStyledCuboid(cuboid);
        return <Cuboid {...styledCuboid} key={cuboid.id} onClick={() => onCuboidClick(cuboid)} />;
      }),
    [cuboids, currentCuboid?.id, onCuboidClick, makeStyledCuboid, isCuboidEditorEnabled]
  );

  const referenceCuboidComp = useMemo(() => {
    if (!referenceCuboid) return undefined;
    const styledCuboid = makeStyledCuboid(referenceCuboid);
    return <Cuboid {...styledCuboid} key={referenceCuboid.id} />;
  }, [referenceCuboid, makeStyledCuboid]);

  const currentCuboidStyle = useMemo(() => {
    if (!currentCuboid) return undefined;
    return styleHandlers.makeCurrentCuboidStyle(currentCuboid);
  }, [currentCuboid, styleHandlers]);

  return (
    <>
      {cuboidsComp}
      {referenceCuboidComp}
      {currentCuboid && (
        <CuboidEditor
          isEnabled={isCuboidEditorEnabled}
          cuboid={currentCuboid}
          setCuboid={setCurrentCuboid}
          cuboidStyle={currentCuboidStyle}
          transformMode={cuboidTransformMode}
          transformSpace={TransformSpace.Local}
          onSaveCuboid={saveCuboid}
          step={resizeStep}
        />
      )}
    </>
  );
};
